<template>
  <div>
    <div v-for="(section, index) in splitContent" :key="index" class="page" :class="pageSizeClass">
      <!--------------------------------- 페이지 헤더 영역 ----------------------------------------->
      <!-- 해더 페이지 번호 영역 -->
      <div v-if="isOddPage(startPageNumber, index)" class="page-number right">
        <!-- 홀수 페이지 번호 -->
        <span class="page-number-text">{{ group?.title }}</span>
        <span>{{ startPageNumber + index }}</span>
      </div>
      <div class="page-number left" v-else>
        <!-- 짝수 페이지 번호 -->
        <span>{{ startPageNumber + index }}</span>
        <span class="page-number-text">{{ item?.title }}</span>
      </div>

      <!-- 헤더 라인 -->
      <div class="page-common-header">
        <hr class="common-line" />
      </div>

      <!--------------------------------- 제목 영역(첫 페이지에만 표시) ----------------------------------------->
      <!-- 제목 -->
      <div v-if="index === 0">
        <div class="title-container">
          <h3 class="title-kor text-bold margin-0" v-if="displayTitleKor">
            {{ displayTitleKor }}<span v-if="titleFootnoteTarget === 'title_kor'">*</span>
          </h3>
          <h3
            class="subtitle-kor text-normal margin-0"
            v-if="displaySubtitleKor"
          >
            {{ displaySubtitleKor }}<span v-if="titleFootnoteTarget === 'subtitle_kor'">*</span>
          </h3>
          <h3 class="title-eng text-bold margin-0" v-if="displayTitleEn">
            {{ displayTitleEn }}<span v-if="titleFootnoteTarget === 'title_en'">*</span>
          </h3>
          <h3
            class="subtitle-eng text-normal margin-0"
            v-if="displaySubtitleEn"
          >
            {{ displaySubtitleEn }}<span v-if="titleFootnoteTarget === 'subtitle_en'">*</span>
          </h3>
        </div>

        <!-- 저자 -->
        <div class="author-container">
          <div class="author-kor text-normal text-right" v-if="displayAuthors">
            {{ displayAuthors }}
          </div>
          <div
            class="author-eng text-normal text-right"
            v-if="displayAuthorEngs"
          >
            {{ displayAuthorEngs }}
          </div>
        </div>
        <hr class="common-line" />

        <!--------------------------------- 본문 영역 ----------------------------------------->
        <!-- 초록 제목 -->
        <div class="content-title-container" v-if="displayAbstractKor">
          <h5 class="content-title text-bold">● 초록(Abstract)</h5>
        </div>
      </div>

      <!-- 본문 -->
      <div class="page-content" v-html="sanitizeHtml(section)"></div>

      <!-- 키워드 (마지막 페이지에만 표시) -->
      <div v-if="index === splitContent.length - 1">
        <div class="keyword-item" v-if="displayKeywordKor">
          <span class="keyword keyword-title">핵심어</span>
          <span class="keyword">
            {{ displayKeywordKor }}
          </span>
        </div>
      </div>

      <!--------------------------------- 푸터 영역 ----------------------------------------->
      <div class="page-common-footer">
        <hr class="common-line" />
      </div>
    </div>

    <!--------------------------------- 템플릿 스타일 측정용 숨겨진 요소 ----------------------------------------->
    <div
      style="
        visibility: hidden;
        position: absolute;
        top: -9999px;
        left: -9999px;
      "
      class="page"
      :class="pageSizeClass"
    >
      <div ref="pageTitleContainerRef">
        <div class="title-container">
          <h3 class="title-kor text-bold margin-0" v-if="displayTitleKor">
            {{ displayTitleKor }}<span v-if="titleFootnoteTarget === 'title_kor'">*</span>
          </h3>
          <h3
            class="subtitle-kor text-normal margin-0"
            v-if="displaySubtitleKor"
          >
            {{ displaySubtitleKor }}<span v-if="titleFootnoteTarget === 'subtitle_kor'">*</span>
          </h3>
          <h3 class="title-eng text-bold margin-0" v-if="displayTitleEn">
            {{ displayTitleEn }}<span v-if="titleFootnoteTarget === 'title_en'">*</span>
          </h3>
          <h3
            class="subtitle-eng text-normal margin-0"
            v-if="displaySubtitleEn"
          >
            {{ displaySubtitleEn }}<span v-if="titleFootnoteTarget === 'subtitle_en'">*</span>
          </h3>
        </div>

        <div class="author-container">
          <div class="author-kor text-normal text-right" v-if="displayAuthors">
            {{ displayAuthors }}
          </div>
          <div
            class="author-eng text-normal text-right"
            v-if="displayAuthorEngs"
          >
            {{ displayAuthorEngs }}
          </div>
        </div>

        <hr class="common-line" />
        <div class="content-title-container" v-if="displayAbstractKor">
          <h5 class="content-title text-bold">● 초록(Abstract)</h5>
        </div>
      </div>

      <div
        ref="pageContentRef"
        class="page-content"
        v-html="sanitizeHtml(item.abstract || item.content)"
      ></div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { getSplitContentByHeight } from "@/components/Templates/heightCalculator";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

export default {
  name: "abstract-paper-page",
  props: {
    item: {
      type: Object,
      required: true,
      default: () => {},
    },
    group: {
      type: Object,
      required: false,
      default: () => {},
    },
    startPageNumber: {
      type: Number,
      required: true,
    },
    pageSize: {
      type: String,
      required: false,
      default: "A4",
    },
  },
  data() {
    return {
      splitContent: [],
    };
  },
  computed: {
    pageSizeClass() {
      return this.pageSize?.toLowerCase() || "a4";
    },
    pageSizeConfig() {
      // 페이지 사이즈별 픽셀 크기 (96 DPI 기준)
      const sizes = {
        a4: { width: 794, height: 1122 },
        a5: { width: 559, height: 794 },
      };
      return sizes[this.pageSizeClass] || sizes.a4;
    },
    displayJournalTitle() {
      return this.item.journal?.title;
    },
    displayTitleKor() {
      return this.item.title;
    },
    displayTitleEn() {
      return this.item.title_eng;
    },
    displaySubtitleKor() {
      return this.item.subtitle;
    },
    displaySubtitleEn() {
      return this.item.subtitle_eng;
    },
    hasTitleFootnote() {
      return (this.item?.comments || []).some((comment) =>
        this.isTitlePageComment(comment)
      );
    },
    titleFootnoteTarget() {
      if (!this.hasTitleFootnote) return null;
      if (this.displaySubtitleKor) return "subtitle_kor";
      if (this.displayTitleKor) return "title_kor";
      if (this.displaySubtitleEn) return "subtitle_en";
      if (this.displayTitleEn) return "title_en";
      return null;
    },
    displayKeywordKor() {
      return this.item.keyword;
    },
    displayAbstractKor() {
      return this.item.abstract ? this.item.abstract : this.item.content;
    },
    displayGroupTitle() {
      return this.group?.title;
    },
    displayAuthors() {
      return this.item?.authors
        ? this.item.authors
            .map((author) => `${author.full_name}(${author.organization})`)
            .join(", ")
        : "";
    },
    displayAuthorEngs() {
      return this.item?.authors
        ? this.item.authors
            .map((author) => `${author.name_eng}(${author.organization_eng})`)
            .join(", ")
        : "";
    },
  },
  methods: {
    sanitizeHtml,
    isTitlePageComment(comment) {
      if (!comment) return false;
      if (comment.page === "title_page") return true;
      return comment.superscript === "*" && Number(comment.page) === 1;
    },
    /** ************************************************************************************
     * 높이에 따른 초록 페이지 분할 처리
     *
     * @param htmlString (String) 페이지 내용
     * @returns (Array<String>) 분할된 페이지 배열
     *************************************************************************************/
    splitContentByHeight(htmlString) {
      // 페이지 사이즈에 따른 동적 설정
      const PAGE_HEIGHT = this.pageSizeConfig.height;
      const PAGE_WIDTH = this.pageSizeConfig.width;
      const HORIZONTAL_PADDING = this.pageSizeClass === 'a5' ? 75 : 123; // A5는 패딩 축소
      const VERTICAL_PADDING = 75 + 30; // (px, 20mm)
      const KEYWORD_HEIGHT = 50; // 키워드 높이 (예시 값)

      return new Promise((resolve) => {
        this.$nextTick(() => {
          // 타이틀 영역 높이 계산
          const titleContainer = this.$refs.pageTitleContainerRef;
          const titleAreaHeight = titleContainer
            ? titleContainer.offsetHeight
            : 0;
          const totalTitleAreaHeight = titleAreaHeight; // 30px는 여유 공간

          const result = getSplitContentByHeight({
            content: htmlString,
            pageWidth: PAGE_WIDTH,
            pageHeight: PAGE_HEIGHT - KEYWORD_HEIGHT, // 키워드 높이를 뺀 페이지 높이
            horizontalPadding: HORIZONTAL_PADDING,
            verticalPadding: VERTICAL_PADDING,
            templateRef: this.$refs.pageContentRef,
            titleAreaHeight: totalTitleAreaHeight,
          });

          resolve(result);
        });
      });
    },
    /** ************************************************************************************
     * 방향 구분 메서드
     *
     * @param startPage (Number) 시작 페이지 번호
     * @param index (Number) 페이지 인덱스
     * @returns (String) 방향 (left, right)
     *************************************************************************************/
    getDirection(startPage, index) {
      const oddDirection = "right";
      const evenDirection = "left";

      return isOddPage(startPage, index) ? oddDirection : evenDirection;
    },

    /** ************************************************************************************
     * 홀수 페이지 여부 확인 메서드
     *
     * @param startPage (Number) 시작 페이지 번호
     * @param index (Number) 페이지 인덱스
     * @returns (Boolean) 홀수 페이지 여부
     *************************************************************************************/
    isOddPage(startPage, index) {
      const page = startPage + index;
      return page % 2 != 0;
    },
    // 폰트 로딩 완료 대기 (일관된 높이 계산을 위해)
    async waitForFonts() {
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) {
          console.warn('Font loading check failed:', e);
        }
      }
      return new Promise(resolve => setTimeout(resolve, 50));
    },
  },
  watch: {
    "item.content": {
      immediate: true,
      async handler(newContent) {
        try {
          await this.waitForFonts();
          const result = await this.splitContentByHeight(newContent);
          this.splitContent = result;
          this.$emit("page-count", this.splitContent.length);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error splitting content:", error);
        }
      },
    },
    "item.abstract": {
      immediate: true,
      async handler(newContent) {
        try {
          await this.waitForFonts();
          const result = await this.splitContentByHeight(newContent);
          this.splitContent = result;
          this.$emit("page-count", this.splitContent.length);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error splitting content:", error);
        }
      },
    },
  },
  async mounted() {
    try {
      await this.waitForFonts();
      const result = await this.splitContentByHeight(
        this.item.abstract ? this.item.abstract : this.item.content
      );
      this.splitContent = result;
      this.$emit("page-count", this.splitContent.length);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error splitting content:", error);
    }
  },
};
</script>

<style lang="scss" scoped>
/** ************************************************************************************
 * 공통 변수
 *************************************************************************************/
$primary-font-color: #000000;
$secondary-font-color: #8b8b8b;
$point-bg-color: #8b8b8b;

// 페이지 사이즈 정의
$a4-width: 794px;
$a4-height: 1122.52px;
$a5-width: 559px;
$a5-height: 794px;

$page-vertical-padding: 75px;
$page-horizontal-padding: 123px;
$page-horizontal-padding-a5: 75px;

$dotum-font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
$batang-font-family: "KoPubWorldBatang", "Noto Sans KR", sans-serif;

/** ************************************************************************************
 * [공통 페이지 스타일]
 *************************************************************************************/

.page {
  background-color: white;
  position: relative;
  box-sizing: border-box;
  overflow: visible;

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
    height: auto;
    padding: $page-vertical-padding $page-horizontal-padding;
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
    height: auto;
    padding: $page-vertical-padding $page-horizontal-padding-a5;
  }
}

.common-line {
  border: none;
  border-top: 0.3mm solid #000;
}

.margin-0 {
  margin: 0;
}

.text-secondary {
  color: $secondary-font-color;
}

.text-bold {
  font-weight: bold;
}

.text-normal {
  font-weight: normal;
}

/** ************************************************************************************
 * [페이지 헤더 스타일]
 *************************************************************************************/

.page-common-header {
  position: absolute;
  top: $page-vertical-padding;
  left: $page-horizontal-padding;
  right: $page-horizontal-padding;
}

/** ************************************************************************************
 * [페이지 푸터 스타일]
 *************************************************************************************/
.page-common-footer {
  position: absolute;
  bottom: $page-vertical-padding;
  left: $page-horizontal-padding;
  right: $page-horizontal-padding;
}

/** ************************************************************************************
 * [페이지 번호 스타일]
 *************************************************************************************/
$page-number-font-size: 13.33px; // 10pt * 1.333
$page-number-font-weight: 400;
$page-number-font-family: $dotum-font-family;

$page-number-text-font-size: 10px; // 7.5pt * 1.333
$page-number-text-font-weight: 400;
$page-number-text-line-height: 33.33px; // 25pt * 1.333
$page-number-text-font-family: $dotum-font-family;

.page-number {
  position: absolute;
  top: $page-vertical-padding - 6mm; // header 라인 겹치지 않게 조절
  left: $page-horizontal-padding;
  right: $page-horizontal-padding;
  font-family: $dotum-font-family;

  font-size: $page-number-font-size;
  font-weight: $page-number-font-weight;
  color: $primary-font-color;

  display: flex;
  align-items: center;
  gap: 7px;

  &.left {
    text-align: left;
  }

  &.right {
    left: auto;
    text-align: right;
  }

  // 하시라 제목
  .page-number-text {
    font-weight: $page-number-text-font-weight;
    font-size: $page-number-text-font-size;
    font-family: $page-number-text-font-family;
  }
}

/** ************************************************************************************
 * [본문 스타일]
 *************************************************************************************/
$content-container-margin: 20px;

$content-font-size: 14px; // 10.5pt * 1.333
$content-line-height: 29.33px; // 22pt * 1.333
$content-letter-spacing: -0.03em;
$content-transform: scaleX(0.98);
$content-margin: 10px;
$content-font-weight: 300;

$content-font-color: $primary-font-color;

.page-content {
  margin-top: $content-container-margin;
  margin-bottom: $content-container-margin;
  color: $content-font-color;

  ::v-deep {
    p {
      font-size: $content-font-size;
      line-height: $content-line-height;
      letter-spacing: $content-letter-spacing;
      transform: $content-transform;
      margin-bottom: $content-margin;
      margin-top: $content-margin;
      font-weight: $content-font-weight;
      word-break: break-all;
    }

    strong {
      font-weight: 700;
    }

    em {
      font-style: italic;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: $primary-font-color;
      border: none !important;
      border-bottom: none !important;
    }

    ol,
    ul {
      li {
        font-size: $content-font-size;
        line-height: $content-line-height;
        letter-spacing: $content-letter-spacing;
        transform: $content-transform;
        font-weight: $content-font-weight;
        margin-left: 5px;
        padding-left: 3px;
        word-break: break-all;
      }
    }

    // 인용문 스타일
    blockquote, .blockquote, .doc-blockquote {
      margin: 12px 0;
      padding: 10px 14px;
      background: #f9fafb;
      border-left: 3px solid #9ca3af;
      font-size: $content-font-size;
      line-height: $content-line-height;
      color: #4b5563;
      text-indent: 0;
    }

    // table-basic table-header-colored 스타일
    table {
      width: auto;
      max-width: 100%;
      border-collapse: collapse;
      border-spacing: 0;
      border: 1px solid #ddd;
      font-size: $content-font-size;
      letter-spacing: $content-letter-spacing;
      transform: $content-transform;
      font-weight: $content-font-weight;

      caption {
        padding: 12px;
        caption-side: top;
        color: #333;
        text-align: center;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.5px;
        border: none !important;
        background: none !important;
      }

      thead th {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
        background-color: #f5f5f5;
        color: #333;
        font-weight: 600;
      }

      tbody th,
      tfoot th {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
        font-weight: normal;
      }

      td {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
        word-break: break-word;
        white-space: normal;
      }

      th {
        padding: 12px 16px;
        border: 1px solid #ddd;
        text-align: left;
        vertical-align: middle;
      }
    }

    // 페이지 나누기 스타일
    .page-break {
      page-break-after: always;
      break-after: page;
      height: 1px;
      margin: 30px 0;
      border-bottom: 1px dashed #ccc;
    }

    // 참고문헌 스타일 - 래퍼 없이 개별 스타일
    .references-title {
      margin-top: 40px;
      font-size: 14px;
      font-weight: 700;
      margin-bottom: 16px;
    }

    .reference-item {
      margin: 6px 0;
      padding-left: 2em;
      text-indent: -2em;
      font-size: 12px;
      line-height: 1.6;
      page-break-inside: avoid;
      break-inside: avoid;
    }
  }
}

/** ************************************************************************************
 * [저자 스타일]
 *************************************************************************************/
$author-margin: 30px;
$author-kor-font-size: 13.33px; // 10pt
$author-kor-line-height: 21.33px; // 16pt
$author-kor-font-family: $dotum-font-family;
$author-kor-color: $primary-font-color;

$author-eng-font-size: 10px; // 8pt
$author-eng-line-height: 15px; // 20pt
$author-eng-font-family: $dotum-font-family;
$author-eng-color: $primary-font-color;

.author-container {
  margin-top: $author-margin;
  margin-bottom: $author-margin;
}

.author-kor {
  font-size: $author-kor-font-size;
  line-height: $author-kor-line-height;
  font-family: $author-kor-font-family;
  color: $author-kor-color;
}

.author-eng {
  font-size: $author-eng-font-size;
  line-height: $author-eng-line-height;
  font-family: $author-eng-font-family;
  color: $author-eng-color;
}

/** ************************************************************************************
 * [키워드 스타일]
 *************************************************************************************/
$keyword-margin: 30px;

$keyword-font-family: $dotum-font-family;
$keyword-font-color: $primary-font-color;

$keyword-font-size: 12.67px; // 9.5pt * 1.333
$keyword-line-height: 24px; // 18pt * 1.333

.keyword-container {
  margin-top: $keyword-margin;
  margin-bottom: $keyword-margin;
}

.keyword-item {
  position: absolute;
  bottom: $page-vertical-padding + 8mm;
  left: $page-horizontal-padding;
  right: $page-horizontal-padding;
}

.keyword {
  font-size: $keyword-font-size;
  line-height: $keyword-line-height;
  font-family: $keyword-font-family;
  color: $keyword-font-color;
}

.keyword-title {
  color: white;
  background-color: $point-bg-color;
  padding: 0px 5px;
}

/** ************************************************************************************
 * [제목 스타일]
 *************************************************************************************/
$title-margin: 30px;

$title-font-family: $dotum-font-family;
$title-font-color: $primary-font-color;

$title-kor-font-size: 21.33px; // 16pt * 1.333
$title-kor-line-height: 26.67px; // 20pt * 1.333

$title-eng-font-size: 14px; // 10.5pt * 1.333
$title-eng-line-height: 20px; // 15pt * 1.333

.title-container {
  margin-top: $title-margin;
  margin-bottom: $title-margin;
  text-align: center;
}

.title-kor {
  font-family: $dotum-font-family;
  color: $primary-font-color;
  font-size: $title-kor-font-size;
  line-height: $title-kor-line-height;
}

.title-eng {
  font-family: $dotum-font-family;
  color: $primary-font-color;
  font-size: $title-eng-font-size;
  line-height: $title-eng-line-height;
}

/** ************************************************************************************
 * [부제목 스타일]
 *************************************************************************************/
$subtitle-font-family: $dotum-font-family;
$subtitle-font-color: $primary-font-color;

$subtitle-kor-font-size: 17.33px; // 13pt * 1.333
$subtitle-kor-line-height: 26.67px; // 20pt * 1.333

$subtitle-eng-font-size: 13.33px; // 10pt * 1.333
$subtitle-eng-line-height: 20px; // 15pt * 1.333

.subtitle-kor {
  font-size: $subtitle-kor-font-size;
  line-height: $subtitle-kor-line-height;
  font-family: $subtitle-font-family;
  color: $subtitle-font-color;
}

.subtitle-eng {
  font-size: $subtitle-eng-font-size;
  line-height: $subtitle-eng-line-height;
  font-family: $subtitle-font-family;
  color: $subtitle-font-color;
}

/** ************************************************************************************
 * [본문 제목 스타일]
 *************************************************************************************/
$content-title-font-family: $dotum-font-family;
$content-title-font-color: $primary-font-color;

$content-title-font-size: 13.33px; // 10pt * 1.333
$content-title-line-height: 29.33px; // 22pt * 1.333

.content-title-container {
  margin-top: 30px;
}

.content-title {
  font-size: $content-title-font-size;
  line-height: $content-title-line-height;
  font-family: $content-title-font-family;
  color: $content-title-font-color;
}

.custom-hr {
  border: none;
  border-top: 0.3mm solid #000;
  margin: 10px 0;
}
</style>
